import React, { useEffect, useState, useCallback } from 'react';
import { useHistory, useLocation, Link } from 'react-router-dom';
import {
  Container,
  Header,
  Input,
  Loader,
  Segment,
  Menu,
  Card,
  Label,
  Pagination,
  Icon,
  Dropdown,
  Button,
  Accordion,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, fetchApi } from '../api';

const PAGE_SIZE = 30;

const CONTENT_TYPE_ICONS = {
  document: 'file text',
  'source-code': 'code',
  presentation: 'desktop',
  specification: 'clipboard',
  research: 'lab',
  binary: 'file',
};

const NAV_GROUP_ICONS = {
  language: 'language',
  b2b: 'handshake',
  regulatory: 'balance scale',
  quality: 'bug',
  design: 'pencil',
  infrastructure: 'server',
  kb: 'book',
  upstream: 'share',
  sessions: 'clock',
  heritage: 'archive',
  'source-archive': 'code',
};

const SOURCE_DIR_LABELS = {
  docs: 'Documentation',
  heritage: 'Heritage',
  'source-archive': 'Source Archive',
  'nick-backend': 'Nick Backend',
};

const DocBrowser = () => {
  const history = useHistory();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const initialCategory = searchParams.get('category') || '';
  const initialSearch = searchParams.get('search') || '';
  const initialPage = parseInt(searchParams.get('page'), 10) || 1;
  const initialContentType = searchParams.get('content_type') || '';
  const initialSourceDir = searchParams.get('source_dir') || '';
  const initialDocumentType = searchParams.get('document_type') || '';
  const initialNavGroup = searchParams.get('nav_group') || '';
  const initialTag = searchParams.get('tag') || '';
  const initialProject = searchParams.get('project') || '';
  const initialCollection = searchParams.get('collection') || '';

  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [navGroups, setNavGroups] = useState([]);
  const [tagsSummary, setTagsSummary] = useState([]);
  const [collectionsSummary, setCollectionsSummary] = useState([]);
  const [contentTypes, setContentTypes] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [sourceDirs, setSourceDirs] = useState([]);
  const [projectsList, setProjectsList] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [category, setCategory] = useState(initialCategory);
  const [contentType, setContentType] = useState(initialContentType);
  const [sourceDir, setSourceDir] = useState(initialSourceDir);
  const [documentType, setDocumentType] = useState(initialDocumentType);
  const [navGroup, setNavGroup] = useState(initialNavGroup);
  const [tag, setTag] = useState(initialTag);
  const [project, setProject] = useState(initialProject);
  const [collection, setCollection] = useState(initialCollection);
  const [search, setSearch] = useState(initialSearch);
  const [searchInput, setSearchInput] = useState(initialSearch);
  const [page, setPage] = useState(initialPage);

  const fetchData = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    if (contentType) params.set('content_type', contentType);
    if (sourceDir) params.set('source_dir', sourceDir);
    if (documentType) params.set('document_type', documentType);
    if (navGroup) params.set('nav_group', navGroup);
    if (tag) params.set('tag', tag);
    if (project) params.set('project', project);
    if (collection) params.set('collection', collection);
    if (search) params.set('search', search);
    params.set('page', page);
    params.set('page_size', PAGE_SIZE);

    fetchApi(`${getApiPath()}/@docs?${params.toString()}`)
      .then((json) => {
        setItems(json.items || []);
        setNavGroups(json.nav_groups || []);
        setCategories(json.categories || []);
        setTagsSummary(json.tags || []);
        setCollectionsSummary(json.collections || []);
        setContentTypes(json.content_types || []);
        setDocumentTypes(json.document_types || []);
        setSourceDirs(json.source_dirs || []);
        setProjectsList(json.projects || []);
        setTotal(parseInt(json.items_total || 0, 10));
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [category, contentType, sourceDir, documentType, navGroup, tag, project, collection, search, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Update URL params
  useEffect(() => {
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    if (contentType) params.set('content_type', contentType);
    if (sourceDir) params.set('source_dir', sourceDir);
    if (documentType) params.set('document_type', documentType);
    if (navGroup) params.set('nav_group', navGroup);
    if (tag) params.set('tag', tag);
    if (project) params.set('project', project);
    if (collection) params.set('collection', collection);
    if (search) params.set('search', search);
    if (page > 1) params.set('page', page);
    const qs = params.toString();
    history.replace(`/docs${qs ? `?${qs}` : ''}`);
  }, [category, contentType, sourceDir, documentType, navGroup, tag, project, collection, search, page, history]);

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const clearFilters = () => {
    setCategory('');
    setContentType('');
    setSourceDir('');
    setDocumentType('');
    setNavGroup('');
    setTag('');
    setProject('');
    setCollection('');
    setSearch('');
    setSearchInput('');
    setPage(1);
  };

  const hasFilters = category || contentType || sourceDir || documentType || navGroup || tag || project || collection || search;

  const totalPages = Math.ceil(total / PAGE_SIZE);

  // Group categories by parent
  const categoryGroups = {};
  categories.forEach((c) => {
    const key = c.category;
    if (!categoryGroups[key]) categoryGroups[key] = { total: 0, subs: [] };
    categoryGroups[key].total += parseInt(c.doc_count, 10);
    if (c.subcategory) {
      categoryGroups[key].subs.push(c);
    }
  });

  return (
    <Container
      id="polyglot-docs"
      style={{ paddingTop: '2em', paddingBottom: '2em' }}
    >
      <Helmet title="Knowledge Base - LJUSA TIDER" />
      <Link to="/polyglot">&larr; Back to Dashboard</Link>

      <div className="polyglot-page-header">
        <h1>LJUSA TIDER Knowledge Base</h1>
        <p className="subtitle">
          {total} documents
          {navGroup && <> in {navGroups.find((g) => g.code === navGroup)?.label || navGroup}</>}
          {sourceDir && <> from {SOURCE_DIR_LABELS[sourceDir] || sourceDir}</>}
          {contentType && <> ({contentType})</>}
          {documentType && <> · class {documentType}</>}
          {tag && <> tagged &ldquo;{tag}&rdquo;</>}
          {collection && <> in collection</>}
        </p>
      </div>

      <Segment secondary size="small" style={{ marginBottom: '1em' }}>
        <Icon name="clipboard list" /> See the{' '}
        <Link to="/qms/register"><strong>QMS Document Register</strong></Link>
        {' '}for controlled documents with traceability and approval trails.
      </Segment>

      <Segment>
        <Input
          placeholder="Search all documents..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') handleSearch();
          }}
          action={{ icon: 'search', onClick: handleSearch }}
          fluid
        />
      </Segment>

      {error && <Segment color="red">Error: {error}</Segment>}

      {/* Show sidebar only when there is sidebar data (guest users get none) */}
      {(sourceDirs.length > 0 || contentTypes.length > 0 || collectionsSummary.length > 0 || tagsSummary.length > 0) ? (
      <div style={{ display: 'flex', gap: '1.5em', maxWidth: '100%', overflow: 'hidden' }}>
        {/* Sidebar */}
        <div style={{ width: '220px', flexShrink: 0 }}>
          {hasFilters && (
            <Button
              size="tiny"
              basic
              fluid
              onClick={clearFilters}
              style={{ marginBottom: '0.5em' }}
            >
              <Icon name="close" /> Clear all filters
            </Button>
          )}

          {/* Topic navigation */}
          {navGroups.length > 0 && (
            <Menu vertical fluid size="small">
              <Menu.Item header>Topics</Menu.Item>
              <Menu.Item
                active={!navGroup}
                onClick={() => { setNavGroup(''); setPage(1); }}
              >
                All Topics
                <Label>{navGroups.reduce((sum, g) => sum + parseInt(g.doc_count, 10), 0)}</Label>
              </Menu.Item>
              {navGroups
                .filter((g) => parseInt(g.doc_count, 10) > 0)
                .map((g) => (
                  <Menu.Item
                    key={g.code}
                    active={navGroup === g.code}
                    onClick={() => { setNavGroup(navGroup === g.code ? '' : g.code); setPage(1); }}
                    title={g.description}
                  >
                    <Icon name={NAV_GROUP_ICONS[g.code] || 'folder'} />
                    {g.label}
                    <Label>{g.doc_count}</Label>
                  </Menu.Item>
                ))}
            </Menu>
          )}

          {/* Content type filter (i18n.docs.content_type — file format/role) */}
          <Menu vertical fluid size="small">
            <Menu.Item header>Type</Menu.Item>
            {contentTypes.map((ct) => (
              <Menu.Item
                key={ct.content_type}
                active={contentType === ct.content_type}
                onClick={() => { setContentType(contentType === ct.content_type ? '' : ct.content_type); setPage(1); }}
              >
                <Icon name={CONTENT_TYPE_ICONS[ct.content_type] || 'file'} />
                {ct.content_type}
                <Label>{ct.doc_count}</Label>
              </Menu.Item>
            ))}
          </Menu>

          {/* Doc Class filter (frontmatter document_type — QMS classification:
              GUIDE, DR, BUG, SOP, WI, etc. — distinct from "Type"/content_type
              above). Per BUG-2026-149. */}
          {documentTypes.length > 0 && (
            <Menu vertical fluid size="small">
              <Menu.Item header>Doc Class</Menu.Item>
              {documentTypes.map((dt) => (
                <Menu.Item
                  key={dt.document_type}
                  active={documentType === dt.document_type}
                  onClick={() => { setDocumentType(documentType === dt.document_type ? '' : dt.document_type); setPage(1); }}
                >
                  {dt.document_type}
                  <Label>{dt.doc_count}</Label>
                </Menu.Item>
              ))}
            </Menu>
          )}

          {/* Collections filter */}
          {collectionsSummary.length > 0 && (
            <Menu vertical fluid size="small">
              <Menu.Item header>Collections</Menu.Item>
              {collectionsSummary
                .filter((c) => parseInt(c.doc_count, 10) > 0)
                .map((c) => (
                  <Menu.Item
                    key={c.code}
                    active={collection === c.code}
                    onClick={() => { setCollection(collection === c.code ? '' : c.code); setPage(1); }}
                  >
                    {c.icon && <Icon name={c.icon} />}
                    {c.title}
                    <Label>{c.doc_count}</Label>
                  </Menu.Item>
                ))}
            </Menu>
          )}

          {/* Tags filter */}
          {tagsSummary.length > 0 && (
            <Segment>
              <Header as="h5">Tags</Header>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {tagsSummary
                  .filter((t) => parseInt(t.doc_count, 10) > 0)
                  .map((t) => (
                    <Label
                      key={t.tag_code}
                      as="a"
                      size="tiny"
                      color={tag === t.tag_code ? t.color || 'blue' : undefined}
                      basic={tag !== t.tag_code}
                      onClick={() => { setTag(tag === t.tag_code ? '' : t.tag_code); setPage(1); }}
                    >
                      {t.label}
                      <Label.Detail>{t.doc_count}</Label.Detail>
                    </Label>
                  ))}
              </div>
            </Segment>
          )}

          {/* Project filter */}
          {projectsList.length > 0 && (
            <Menu vertical fluid size="small">
              <Menu.Item header>Projects</Menu.Item>
              {projectsList.slice(0, 15).map((p) => (
                <Menu.Item
                  key={p.project}
                  active={project === p.project}
                  onClick={() => { setProject(project === p.project ? '' : p.project); setPage(1); }}
                >
                  {p.project}
                  <Label>{p.doc_count}</Label>
                </Menu.Item>
              ))}
            </Menu>
          )}

          {/* View modes */}
          <Segment>
            <Button.Group size="tiny" fluid>
              <Button as={Link} to="/docs" active>
                <Icon name="list" /> List
              </Button>
              <Button as={Link} to="/docs/timeline">
                <Icon name="clock" /> Timeline
              </Button>
            </Button.Group>
          </Segment>
        </div>

        {/* Main content */}
        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          {loading ? (
            <Loader active inline="centered" content="Loading documents..." />
          ) : !navGroup && !search && !tag && !category && !collection && !documentType ? (
            /* No filter selected — show topic overview, not the pile */
            <>
              <Header as="h2">Browse by Topic</Header>
              <p style={{ color: '#666', marginBottom: '1.5em' }}>
                Select a topic to browse its documents, or use search to find something specific.
              </p>
              <Card.Group itemsPerRow={2} stackable>
                {navGroups
                  .filter((g) => parseInt(g.doc_count, 10) > 0)
                  .map((g) => (
                    <Card
                      key={g.code}
                      onClick={() => { setNavGroup(g.code); setPage(1); }}
                      style={{ cursor: 'pointer' }}
                    >
                      <Card.Content>
                        <Card.Header>
                          <Icon name={NAV_GROUP_ICONS[g.code] || 'folder'} color="blue" />
                          {' '}{g.label}
                        </Card.Header>
                        <Card.Meta>{g.doc_count} documents</Card.Meta>
                        <Card.Description style={{ color: '#666', fontSize: '0.9em' }}>
                          {g.description}
                        </Card.Description>
                      </Card.Content>
                    </Card>
                  ))}
              </Card.Group>
            </>
          ) : (
            <>
              <Card.Group itemsPerRow={1}>
                {items.map((doc) => (
                  <Card
                    key={doc.slug}
                    as={Link}
                    to={`/docs/${doc.slug}`}
                    fluid
                  >
                    <Card.Content>
                      <Card.Header>
                        <Icon
                          name={CONTENT_TYPE_ICONS[doc.content_type] || 'file'}
                          style={{ marginRight: '0.4em', opacity: 0.6 }}
                        />
                        {doc.doc_id && (
                          <Label size="tiny" color="blue" style={{ marginRight: '0.5em', verticalAlign: 'middle' }}>
                            {doc.doc_id}
                          </Label>
                        )}
                        {doc.title}
                      </Card.Header>
                      <Card.Meta>
                        {doc.document_type && (
                          <Label size="tiny" color="violet">
                            {doc.document_type}
                          </Label>
                        )}
                        <Label size="tiny" color="blue">
                          {doc.category}
                        </Label>
                        {doc.subcategory && (
                          <Label size="tiny">{doc.subcategory}</Label>
                        )}
                        {doc.source_dir && doc.source_dir !== 'docs' && (
                          <Label size="tiny" color="grey">
                            {SOURCE_DIR_LABELS[doc.source_dir] || doc.source_dir}
                          </Label>
                        )}
                        {doc.project && (
                          <Label size="tiny" color="teal">
                            {doc.project}
                          </Label>
                        )}
                        {doc.status && (
                          <Label size="tiny" color="olive">
                            {doc.status}
                          </Label>
                        )}
                        {doc.content_type && doc.content_type !== 'document' && (
                          <Label size="tiny" basic>
                            {doc.content_type}
                          </Label>
                        )}
                        {doc.author && (
                          <span style={{ marginLeft: '0.5em' }}>
                            <Icon name="user" />
                            {doc.author}
                          </span>
                        )}
                        {doc.doc_date && (
                          <span style={{ marginLeft: '0.5em' }}>
                            <Icon name="calendar" />
                            {doc.doc_date}
                          </span>
                        )}
                      </Card.Meta>
                      <Card.Description
                        style={{ color: '#888', fontSize: '0.9em' }}
                      >
                        {doc.git_date && (
                          <span style={{ marginRight: '0.8em' }}>
                            <Icon name="clock outline" />
                            {new Date(doc.git_date).toLocaleDateString('en-CA')}
                          </span>
                        )}
                        {doc.file_size
                          ? `${(doc.file_size / 1024).toFixed(1)} KB`
                          : ''}
                        {doc.file_extension && ` (${doc.file_extension})`}
                        {doc.git_author &&
                          ` \u00b7 Last edited by ${doc.git_author}`}
                        {doc.slug && (
                          <div style={{ marginTop: '0.3em', color: '#aaa', fontSize: '0.85em', fontFamily: 'monospace' }}>
                            docs/{doc.slug}{doc.file_extension || '.md'}
                          </div>
                        )}
                      </Card.Description>
                    </Card.Content>
                  </Card>
                ))}
                {items.length === 0 && (
                  <Segment textAlign="center">
                    No documents found.
                    {hasFilters && (
                      <>
                        {' '}
                        <a href="#" onClick={(e) => { e.preventDefault(); clearFilters(); }}>
                          Clear filters
                        </a>
                      </>
                    )}
                  </Segment>
                )}
              </Card.Group>

              {totalPages > 1 && (
                <div style={{ textAlign: 'center', marginTop: '1em' }}>
                  <Pagination
                    activePage={page}
                    totalPages={totalPages}
                    onPageChange={(e, { activePage }) => setPage(activePage)}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
      ) : (
      /* No sidebar — guest user view: grouped card layout */
      <div>
        {loading ? (
          <Loader active inline="centered" content="Loading documents..." />
        ) : (
          <>
            {(() => {
              // Group documents into categories for the curated public view
              const groups = [
                {
                  title: 'ISO 704 & Concepts',
                  icon: 'sitemap',
                  color: 'purple',
                  description: 'Concept modelling framework for translation message semantics',
                  match: (d) => /iso.704|concept|definition.*needs.*definition/i.test(d.title),
                },
                {
                  title: 'Platform',
                  icon: 'server',
                  color: 'teal',
                  description: 'Plone, Volto, and Nick CMS architecture and ecosystem',
                  match: (d) => /ecosystem|volto story/i.test(d.title),
                },
                {
                  title: 'Reference',
                  icon: 'book',
                  color: 'blue',
                  description: 'Standards, language data, character sets, export formats, and workflows',
                  match: (d) => true, // catch-all for remaining docs
                },
              ];

              const used = new Set();
              const grouped = groups.map((g) => {
                const docs = items.filter((d) => {
                  if (used.has(d.slug)) return false;
                  if (g.match(d)) {
                    used.add(d.slug);
                    return true;
                  }
                  return false;
                });
                return { ...g, docs };
              });

              return grouped
                .filter((g) => g.docs.length > 0)
                .map((g) => (
                  <div key={g.title} style={{ marginBottom: '2em' }}>
                    <Header as="h2" dividing>
                      <Icon name={g.icon} color={g.color} />
                      <Header.Content>
                        {g.title}
                        <Header.Subheader>{g.description}</Header.Subheader>
                      </Header.Content>
                    </Header>
                    <Card.Group itemsPerRow={1}>
                      {g.docs.map((doc) => (
                        <Card
                          key={doc.slug}
                          as={Link}
                          to={`/docs/${doc.slug}`}
                          fluid
                        >
                          <Card.Content>
                            <Card.Header>
                              {doc.doc_id && (
                                <Label size="tiny" color={g.color} style={{ marginRight: '0.5em', verticalAlign: 'middle' }}>
                                  {doc.doc_id}
                                </Label>
                              )}
                              {doc.title}
                            </Card.Header>
                            <Card.Meta>
                              {doc.author && (
                                <span>
                                  <Icon name="user" /> {doc.author}
                                </span>
                              )}
                              {doc.doc_date && (
                                <span style={{ marginLeft: '0.5em' }}>
                                  <Icon name="calendar" /> {doc.doc_date}
                                </span>
                              )}
                            </Card.Meta>
                          </Card.Content>
                        </Card>
                      ))}
                    </Card.Group>
                  </div>
                ));
            })()}
            {items.length === 0 && (
              <Segment textAlign="center">No documents found.</Segment>
            )}
          </>
        )}
      </div>
      )}
    </Container>
  );
};

export default DocBrowser;
